import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { LANGUAGE_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
} from '@/api/common';
import { Language } from '@/api/enum/common.enum';
import { AreaDTO } from '@/api/DTO/common.interface';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditContactInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const [getAreaSubItemList] = useGetAreaSubItemListMutation();
		const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
		const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
		const [estateCourtVillageList, setEstateCourtVillageList] = useState<
			AreaDTO[]
		>([]);
		const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);

		const { data: housingTypeDicList } = useGetDicListQuery('housing_type', {
			refetchOnMountOrArgChange: true,
		});

		const { data: areaItemList } = useGetAreaItemListQuery(
			{ type: 0 },
			{ refetchOnMountOrArgChange: true }
		);

		useEffect(() => {
			if (details) {
				methods.reset(details);

				if (details.region) initAddress(details.region, 'region');
				if (details.district) initAddress(details.district, 'district');
				if (details.subdistrict)
					initAddress(details.subdistrict, 'subdistrict');
				if (details.estateCourtVillage)
					initAddress(details.estateCourtVillage, 'estateCourtVillage');
			}
		}, [details]);

		const defaultValues: DefaultValues<any> = {
			addressLanguage: Language.chinese,
		};

		const formSchema = Yup.object().shape({
			phone: Yup.string()
				.required(t('errorMessage.pleaseEnter'))
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			homeTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			otherPhone: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
			typeOfHousingSpecify: Yup.string().when('typeOfHousing', {
				is: (typeOfHousing: string) => typeOfHousing && housingTypeIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchAddressLanguage = methods.watch('addressLanguage');
		const watchTypeOfHousing = methods.watch('typeOfHousing');

		const housingTypeIsOther = () => {
			const obj = housingTypeDicList?.data.find(
				(item: any) => item.id == watchTypeOfHousing
			);
			if (obj && obj.labelEn === 'Others') return true;
			return false;
		};

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const initAddress = async (pid: string, type: string) => {
			if (!pid) return;
			const response = await getAreaSubItemList(pid || '');
			if ('data' in response) {
				if (type == 'region') setDistrictList(response?.data?.data || []);
				if (type == 'district') setSubDistrictList(response?.data?.data || []);
				if (type == 'subdistrict')
					setEstateCourtVillageList(response?.data?.data || []);
				if (type == 'estateCourtVillage')
					setBuildingBlockList(response?.data?.data || []);
			}
		};

		const handleChangeRegion = async (e: any, type: string) => {
			if (!e) return;
			const response = await getAreaSubItemList(e || '');

			if ('data' in response) {
				if (type == 'region') {
					methods.setValue('district', '');
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setDistrictList(response?.data?.data || []);
				}
				if (type == 'district') {
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setSubDistrictList(response?.data?.data || []);
				}
				if (type == 'subdistrict') {
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setEstateCourtVillageList(response?.data?.data || []);
				}
				if (type == 'estateCourtVillage') {
					methods.setValue('buildingBlock', '');
					setBuildingBlockList(response?.data?.data || []);
				}
			}
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.contactInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='phone'
									label={t('preAdmission.mobilePhone') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='homeTel'
									label={t('preAdmission.homeTel')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='otherPhone'
									label={t('preAdmission.otherPhone')}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='typeOfHousing'
									label={t('preAdmission.typeHousing')}
									options={(housingTypeDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('typeOfHousingSpecify', '');

										methods.clearErrors('typeOfHousingSpecify');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!housingTypeIsOther()}
									control={methods.control}
									name='typeOfHousingSpecify'
									label={`${t('preAdmission.typeHousingSpecify')}${
										housingTypeIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.addressLanguage') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='addressLanguage'
									row
									options={LANGUAGE_LIST}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.region')}
											options={(areaItemList?.data || []).map(
												(item: { nameEn: any }) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.district')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.subDistrict')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate/court/village')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}
							{watchAddressLanguage == Language.chinese && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.region')}
											options={(areaItemList?.data || []).map(
												(item: { nameZh: any }) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.district')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.subDistrict')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate/court/village')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreet'
									label={t('preAdmission.road/street')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreetNo'
									label={t('preAdmission.road/streetNo')}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.building/block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameEn,
											};
										})}
									/>
								</Grid>
							)}
							{watchAddressLanguage == Language.chinese && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.building/block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameZh,
											};
										})}
									/>
								</Grid>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='buildingBlockSpecify'
									label={t('preAdmission.building/block(specify)')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='floor'
									label={t('preAdmission.floor')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='flat'
									label={t('preAdmission.flat')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditContactInformationForm.displayName = 'EditContactInformationForm';

export default EditContactInformationForm;
